import React from 'react';
import { ExclamationTriangleIcon, XMarkIcon } from '@heroicons/react/24/outline';

interface ConfirmDialogProps {
  isOpen: boolean;
  title: string;
  message: string;
  confirmLabel: string;
  cancelLabel: string;
  onConfirm: () => void;
  onCancel: () => void;
  variant?: 'danger' | 'warning';
  disabled?: boolean;
}

const ConfirmDialog: React.FC<ConfirmDialogProps> = ({
  isOpen,
  title,
  message,
  confirmLabel,
  cancelLabel,
  onConfirm,
  onCancel,
  variant = 'warning',
  disabled = false,
}) => {
  if (!isOpen) return null;

  const variantStyles = {
    danger: {
      icon: 'text-red-600',
      title: 'text-red-600',
      button: 'bg-red-600 hover:bg-red-700'
    },
    warning: {
      icon: 'text-orange-600',
      title: 'text-orange-600',
      button: 'bg-orange-600 hover:bg-orange-700'
    }
  }[variant];

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
        <div className="fixed inset-0 bg-black/30 transition-opacity" onClick={onCancel} />
        
        <div className="relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg">
          <div className="absolute right-0 top-0 pr-4 pt-4">
            <button
              type="button"
              className="rounded-md bg-white text-gray-400 hover:text-gray-500"
              onClick={onCancel}
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>
          
          <div className="p-6">
            <div className="flex items-center">
              <div className={`mr-3 ${variantStyles.icon}`}>
                <ExclamationTriangleIcon className="h-6 w-6" />
              </div>
              <h3 className={`text-lg font-semibold ${variantStyles.title}`}>
                {title}
              </h3>
            </div>
            
            <p className="mt-4 text-neutral-600">
              {message}
            </p>

            <div className="mt-6 flex justify-end space-x-4">
              <button
                type="button"
                className="px-4 py-2 text-sm font-medium text-neutral-700 hover:bg-neutral-50 rounded-md"
                onClick={onCancel}
              >
                {cancelLabel}
              </button>
              <button
                type="button"
                className={`px-4 py-2 text-sm font-medium text-white rounded-md ${variantStyles.button} ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
                onClick={onConfirm}
                disabled={disabled}
              >
                {disabled ? 'Traitement…' : confirmLabel}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConfirmDialog;